import { type EvaluationContext } from "@openfeature/web-sdk";

import { Region } from "../graphql";

/**
 * Subset of the Keycloak access token claims we read to build the OpenFeature
 * evaluation context. `@gportal/keycloak-auth` types `accessTokenParsed` with a
 * minimal shape, but the raw token carries these additional claims.
 */
export interface FeatureFlagClaims {
  sub?: string;
  email?: string;
  preferred_username?: string;
  groups?: string[];
  address?: { country?: string };
}

/**
 * Maps the NGP region (EU/US/DEV) onto the region dimension the website sends to
 * GO Feature Flag ("EUR"/"INT"). This MUST match the website so the same user
 * gets the same flag results. DEV has no website analogue and falls back to EUR.
 */
export const regionToFlagRegion = (region: Region | undefined): string => {
  switch (region) {
    case Region.Us:
      return "INT";
    case Region.Eu:
    case Region.Dev:
    default:
      return "EUR";
  }
};

/**
 * Pure builder for the evaluation context. Mirrors the website's `buildContext`
 * (app/plugins/openfeature.client.ts) field-for-field so the same user gets the
 * same flag results. Kept free of singletons (auth/router) so it can be tested.
 *
 * NGP is always authenticated (login-required), so there is no anonymous /
 * anon-cookie branch and no IP-derived country fallback — the Keycloak
 * `address.country` claim is the sole source, exactly as the website resolves it
 * for logged-in users.
 */
export const buildEvaluationContext = (
  claims: FeatureFlagClaims,
  region: Region | undefined,
  environment: string,
): EvaluationContext => {
  const context: EvaluationContext = {
    targetingKey: claims.sub ?? "",
    environment,
    region: regionToFlagRegion(region),
  };

  // groups are serialised as ",a,b,c," so targeting rules can do exact token
  // matching via `user.groups co ",gp-admin,"` without prefix collisions
  // (mirrors the website and the API's getEvaluationContext).
  const groupClaims = claims.groups ?? [];
  const groups = groupClaims.length > 0 ? `,${groupClaims.join(",")},` : ",";

  const country = (claims.address?.country ?? "").toUpperCase();

  context.user = {
    id: claims.sub ?? "",
    email: claims.email ?? "",
    username: claims.preferred_username ?? "",
    groups,
    ...(country ? { country } : {}),
  };

  return context;
};
